import type { CSSProperties } from 'react';
import type { NoteFigure } from '../types';
import { FIGURE_LABELS } from '../types';
import type { RhythmPattern } from '../utils/rhythmPattern';
import { globalCellIndex } from '../utils/pattern';
import { rhythmToMelodyPattern } from '../utils/rhythmPlayback';
import type { BeatFeedbackStatus } from '../utils/rhythmCompare';
import { RhythmCell } from './RhythmCell';
import { FigureIcon } from './FigureIcon';

const BEAT_COLORS = [
  '#FF5252',
  '#FF9D00',
  '#22C55E',
  '#2DA8F5',
  '#A855F7',
  '#EC4899',
  '#14B8A6',
];

interface Props {
  pattern: RhythmPattern;
  accentColor: string;
  activeIndex: number | null;
  selectedFigure: NoteFigure;
  visibleMeasures?: number[];
  beatFeedback?: Record<string, BeatFeedbackStatus>;
  revealAnswer?: RhythmPattern;
  readOnly?: boolean;
  discoverLayout?: boolean;
  onCellToggle: (
    measureIndex: number,
    beatIndex: number,
    cellIndex: number,
  ) => void;
  onApplyFigure: (measureIndex: number, beatIndex: number) => void;
}

export function RhythmPatternGrid({
  pattern,
  accentColor,
  activeIndex,
  selectedFigure,
  visibleMeasures,
  beatFeedback,
  revealAnswer,
  readOnly = false,
  discoverLayout = false,
  onCellToggle,
  onApplyFigure,
}: Props) {
  const melodyView = rhythmToMelodyPattern(pattern);
  const indices = visibleMeasures ?? pattern.measures.map((_, i) => i);

  const gridInner = indices.map((mi) => {
        const measure = pattern.measures[mi];
        if (!measure) return null;

        return (
          <section key={mi} className="measure-block" aria-label={`Compás ${mi + 1}`}>
            <div className="measure-label">
              {discoverLayout ? `C. ${mi + 1}` : `Compás ${mi + 1}`}
            </div>
            <div className="measure-beats">
              {measure.slots.map((slot, bi) => {
                if (!slot.active) return null;

                const fbKey = `${mi}-${bi}`;
                const feedback = beatFeedback?.[fbKey];
                const answerSlot = revealAnswer?.measures[mi]?.slots[bi];
                const showReveal = feedback === 'wrong' && answerSlot?.active;

                const spanClass =
                  slot.span > 1 ? `beat-slot--span-${slot.span}` : '';
                const count = slot.cells.length;
                const color = BEAT_COLORS[bi % BEAT_COLORS.length];

                return (
                  <div
                    key={bi}
                    className={`beat-slot beat-slot--rhythm ${spanClass} ${feedback ? `beat-slot--${feedback}` : ''}`}
                    style={
                      {
                        flex: slot.span,
                        '--beat-color': color,
                      } as CSSProperties
                    }
                  >
                    <button
                      type="button"
                      className="beat-slot__header"
                      onClick={() => !readOnly && onApplyFigure(mi, bi)}
                      disabled={readOnly}
                      title={
                        readOnly
                          ? undefined
                          : `Aplicar ${FIGURE_LABELS[selectedFigure]} a este tiempo`
                      }
                    >
                      <span className="beat-slot__time">
                        {discoverLayout ? `T${bi + 1}` : `Tiempo ${bi + 1}`}
                      </span>
                      <span className="beat-slot__figure">
                        {discoverLayout
                          ? FIGURE_LABELS[slot.figure].slice(0, 3)
                          : FIGURE_LABELS[slot.figure]}
                      </span>
                      {feedback === 'correct' && (
                        <span className="beat-slot__badge beat-slot__badge--ok">✓</span>
                      )}
                      {feedback === 'wrong' && (
                        <span className="beat-slot__badge beat-slot__badge--bad">✗</span>
                      )}
                    </button>
                    <div
                      key={slot.subdivideKey}
                      className={`beat-slot__cells beat-slot__cells--n${count} ${!readOnly ? 'beat-slot__cells--pop' : ''}`}
                    >
                      {slot.cells.map((cell, ci) => {
                        const gIdx = globalCellIndex(mi, bi, ci, melodyView);
                        return (
                          <RhythmCell
                            key={ci}
                            figure={cell.figure}
                            hit={cell.hit}
                            active={activeIndex === gIdx}
                            compact={discoverLayout || count > 2}
                            accentColor={accentColor}
                            onClick={() => !readOnly && onCellToggle(mi, bi, ci)}
                          />
                        );
                      })}
                    </div>
                    {showReveal && answerSlot && !discoverLayout && (
                      <div className="beat-slot__reveal" aria-label="Respuesta correcta">
                        <span className="beat-slot__reveal-label">Era:</span>
                        <FigureIcon figure={answerSlot.figure} />
                        <span className="beat-slot__reveal-hits">
                          {answerSlot.cells.map((c, i) => (
                            <span
                              key={i}
                              className={c.hit ? 'reveal-hit' : 'reveal-rest'}
                            >
                              {c.hit ? '♪' : '·'}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
  });

  return (
    <div
      className={`pattern-grid pattern-grid--rhythm ${discoverLayout ? 'pattern-grid--discover-fit' : ''}`}
    >
      {discoverLayout ? (
        <div
          className="discover-measures-grid"
          data-count={Math.min(indices.length, 4)}
        >
          {gridInner}
        </div>
      ) : (
        gridInner
      )}
    </div>
  );
}
